import { type ReactNode } from 'react';
import {
  FolderOpenIcon,
  MagnifyingGlassIcon,
  DocumentIcon,
  CalendarDaysIcon,
  HeartIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';

type EmptyStateVariant =
  | 'default'
  | 'search'
  | 'document'
  | 'calendar'
  | 'gratitude'
  | 'messages';

interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

const defaultIcons: Record<EmptyStateVariant, typeof FolderOpenIcon> = {
  default: FolderOpenIcon,
  search: MagnifyingGlassIcon,
  document: DocumentIcon,
  calendar: CalendarDaysIcon,
  gratitude: HeartIcon,
  messages: ChatBubbleBottomCenterTextIcon,
};

export function EmptyState({
  variant = 'default',
  title,
  description,
  icon,
  action,
  className = '',
}: EmptyStateProps) {
  const Icon = defaultIcons[variant];

  return (
    <div
      className={`
        flex flex-col items-center justify-center
        py-12 px-6 text-center
        ${className}
      `}
    >
      {/* Icon container with subtle background */}
      <div
        className="
          w-16 h-16 rounded-full mb-6
          bg-surface-100 dark:bg-surface-800
          flex items-center justify-center
        "
      >
        {icon || (
          <Icon className="w-8 h-8 text-surface-400 dark:text-surface-500" />
        )}
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-2">
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p className="text-surface-500 dark:text-surface-400 max-w-sm mb-6">
          {description}
        </p>
      )}

      {/* Action */}
      {action && <div>{action}</div>}
    </div>
  );
}

// Preset empty states for common use cases
export function NoSearchResults({
  searchTerm,
  onClear,
}: {
  searchTerm: string;
  onClear?: () => void;
}) {
  return (
    <EmptyState
      variant="search"
      title="No results found"
      description={`We couldn't find anything matching "${searchTerm}". Try a different search term.`}
      action={
        onClear && (
          <button
            onClick={onClear}
            className="text-accent-600 hover:text-accent-700 dark:text-accent-400 font-medium"
          >
            Clear search
          </button>
        )
      }
    />
  );
}

export function NoData({
  title = 'No data yet',
  description = 'Start adding items to see them here.',
  actionLabel,
  onAction,
}: {
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <EmptyState
      variant="default"
      title={title}
      description={description}
      action={
        actionLabel &&
        onAction && (
          <button onClick={onAction} className="btn-primary">
            {actionLabel}
          </button>
        )
      }
    />
  );
}

export function NoGratitude({ onAdd }: { onAdd?: () => void }) {
  return (
    <EmptyState
      variant="gratitude"
      title="Start your gratitude practice"
      description="Taking a moment each day to recognize what you're grateful for can transform your perspective."
      action={
        onAdd && (
          <button onClick={onAdd} className="btn-accent">
            Add gratitude
          </button>
        )
      }
    />
  );
}

export function NoMessages() {
  return (
    <EmptyState
      variant="messages"
      title="No messages"
      description="Messages from your sponsor will appear here."
    />
  );
}

export default EmptyState;
